import React, {createContext, lazy, Suspense, useCallback, useEffect, useMemo} from "react";

import {ConfigProvider} from 'antd';
import antdTR from "antd/lib/locale-provider/tr_TR";
import antdUS from "antd/lib/locale-provider/en_US";
import antdAZ from "antd/lib/locale-provider/az_AZ";
import antdRU from "antd/lib/locale-provider/ru_RU";
import customParseFormat from 'dayjs/plugin/customParseFormat';
import calendarSystems from "@calidy/dayjs-calendarsystems";
import HijriCalendarSystem from "@calidy/dayjs-calendarsystems/calendarSystems/HijriCalendarSystem";


import {useDispatch, useSelector} from "react-redux";

import {BrowserRouter, Navigate, Route, Routes,} from "react-router-dom";

import {
    LAYOUT_TYPE_BOXED,
    LAYOUT_TYPE_FRAMED,
    LAYOUT_TYPE_FULL,
    NAV_STYLE_ABOVE_HEADER,
    NAV_STYLE_BELOW_HEADER,
    NAV_STYLE_DARK_HORIZONTAL,
    NAV_STYLE_DEFAULT_HORIZONTAL,
    NAV_STYLE_INSIDE_HEADER_HORIZONTAL,
    THEME_TYPE_DARK
} from "constants/ThemeSetting";
import ApiSpinner from "core/components/apiSpinner/ApiSpinner";
import dayjs from "dayjs";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localeData from "dayjs/plugin/localeData";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import updateLocale from "dayjs/plugin/updateLocale";
import tr from "dayjs/locale/tr";
import uz from "dayjs/locale/uz-latn";
import ar from "dayjs/locale/ar";
import en from "dayjs/locale/en";
import az from "dayjs/locale/az";
import ru from "dayjs/locale/ru";
import CircularProgress from "./core/components/apiSpinner/CircularProgress";
import {changeStoreLanguage} from "./redux/redusers/localizationStoreSlice";
import {RootState} from "./redux/store";
import AppInfoGetter from "./core/app/AppInfoGetter";
import uzLatnUZ from "./locale/uz-Latn-UZ";
import arQA from "./locale/ar-QA";
import {AppInfo} from "./core/app/interface";
import {getSubProject} from "./redux/redusers/authSlice";
import {defaultSubProjectName} from "./containers/login/interface";
import {getSubProjectFromTitle, getSubProjectInfo} from "./containers/login/loginHelper";
import {setDirectionRTL} from "redux/redusers/themeSlice";
import {I18nProvider} from "./i18n/I18nProvider";
import {EnumProvider} from "./context/enum/EnumProvider";
import {MinoNotificationProvider} from "./core/components/notification/MinoNotificationProvider";

const Main = lazy(() => import('containers/main/Main'));
const Login = lazy(() => import('containers/login/Login'));

const setLayoutType = (layoutType: string) => {
    if (layoutType === LAYOUT_TYPE_FULL) {
        document.body.classList.remove('boxed-layout');
        document.body.classList.remove('framed-layout');
        document.body.classList.add('full-layout');
    } else if (layoutType === LAYOUT_TYPE_BOXED) {
        document.body.classList.remove('full-layout');
        document.body.classList.remove('framed-layout');
        document.body.classList.add('boxed-layout');
    } else if (layoutType === LAYOUT_TYPE_FRAMED) {
        document.body.classList.remove('boxed-layout');
        document.body.classList.remove('full-layout');
        document.body.classList.add('framed-layout');
    }
};

const setNavStyle = (navStyle: string) => {
    if (navStyle === NAV_STYLE_DEFAULT_HORIZONTAL ||
        navStyle === NAV_STYLE_DARK_HORIZONTAL ||
        navStyle === NAV_STYLE_INSIDE_HEADER_HORIZONTAL ||
        navStyle === NAV_STYLE_ABOVE_HEADER ||
        navStyle === NAV_STYLE_BELOW_HEADER) {
        document.body.classList.add('full-scroll');
        document.body.classList.add('horizontal-layout');
    } else {
        document.body.classList.remove('full-scroll');
        document.body.classList.remove('horizontal-layout');
    }
};

const styleSheetLink = document.createElement('link');
styleSheetLink.type = 'text/css';
styleSheetLink.rel = 'stylesheet';
document.body.appendChild(styleSheetLink);


export const AppContext = createContext<object>({});

const App = () => {

    const {isDirectionRTL, navStyle, layoutType, themeColor, themeType} = useSelector(({theme}: RootState) => theme);
    const {token} = useSelector(({auth}: any) => auth);
    const {locale, languageCode} = useSelector(({localization}: RootState) => localization);

    const dispatch = useDispatch();

    let antdLocale: any = undefined;
    ///https://github.com/iamkun/dayjs/blob/dev/src/locale/tr.js
    dayjs.extend(LocalizedFormat);
    dayjs.extend(advancedFormat)
    dayjs.extend(localeData);
    dayjs.extend(updateLocale);
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(customParseFormat);
    dayjs.extend(calendarSystems);
    if (languageCode === "tr") {
        dispatch(setDirectionRTL(false));
        antdLocale = antdTR;
        dayjs.locale(tr);
        dayjs.updateLocale("tr", {
            formats: {
                LT: 'HH:mm:ss',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D MMMM YYYY',
                LLL: 'D MMMM YYYY HH:mm',
                LLLL: 'dddd, D MMMM YYYY HH:mm'
            }
        })
    } else if (languageCode === "uz" || languageCode === "uz-Latn") {
        dispatch(setDirectionRTL(false));
        antdLocale = uzLatnUZ;
        dayjs.locale(uz);
        dayjs.updateLocale("uz-Latn", {
            formats: {
                LT: 'HH:mm:ss',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D MMMM YYYY',
                LLL: 'D MMMM YYYY HH:mm',
                LLLL: 'dddd, D MMMM YYYY HH:mm'
            }
        })
        dayjs.updateLocale("uz-Latn-UZ", {
            formats: {
                LT: 'HH:mm:ss',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D MMMM YYYY',
                LLL: 'D MMMM YYYY HH:mm',
                LLLL: 'dddd, D MMMM YYYY HH:mm'
            }
        })
        dayjs.updateLocale("uz", {
            formats: {
                LT: 'HH:mm:ss',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D MMMM YYYY',
                LLL: 'D MMMM YYYY HH:mm',
                LLLL: 'dddd, D MMMM YYYY HH:mm'
            }
        })
    } else if (languageCode === "ar") {
        dispatch(setDirectionRTL(true));
        if (locale == "ar-QA") {
            antdLocale = arQA;
            dayjs.locale(ar);
            dayjs.registerCalendarSystem("islamic", new HijriCalendarSystem());
            dayjs.updateLocale("ar", {
                formats: {
                    LT: 'HH:mm:ss',
                    LTS: 'HH:mm:ss',
                    L: 'DD.MM.YYYY',
                    LL: 'D MMMM YYYY',
                    LLL: 'D MMMM YYYY HH:mm',
                    LLLL: 'dddd, D MMMM YYYY HH:mm'
                }
            })

        }

    } else if (languageCode === "en") {
        dispatch(setDirectionRTL(false));
        antdLocale = antdUS;
        dayjs.locale(en);
        dayjs.updateLocale("en", {
            formats: {
                LT: 'HH:mm:ss',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D MMMM YYYY',
                LLL: 'D MMMM YYYY HH:mm',
                LLLL: 'dddd, D MMMM YYYY HH:mm'
            }
        })
    } else if (languageCode === "az" || languageCode === "az-Latn") {
        dispatch(setDirectionRTL(false));
        dayjs.locale(az);
        antdLocale = antdAZ;
        dayjs.updateLocale("az", {
            formats: {
                LT: 'HH:mm:ss',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D MMMM YYYY',
                LLL: 'D MMMM YYYY HH:mm',
                LLLL: 'dddd, D MMMM YYYY HH:mm'
            }
        })
    } else if (languageCode === "ru") {
        dispatch(setDirectionRTL(false));
        dayjs.locale(ru);
        antdLocale = antdRU;
        dayjs.updateLocale("ru", {
            formats: {
                LT: 'HH:mm:ss',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D MMMM YYYY',
                LLL: 'D MMMM YYYY HH:mm',
                LLLL: 'dddd, D MMMM YYYY HH:mm'
            }
        })
    } else {
        dispatch(setDirectionRTL(false));
        antdLocale = antdTR;
        dayjs.locale(tr);
        dayjs.updateLocale("tr", {
            formats: {
                LT: 'HH:mm:ss',
                LTS: 'HH:mm:ss',
                L: 'DD.MM.YYYY',
                LL: 'D MMMM YYYY',
                LLL: 'D MMMM YYYY HH:mm',
                LLLL: 'dddd, D MMMM YYYY HH:mm'
            }
        })
    }


    const keyDownEvent = useMemo(() => {
        return new Map<string, (e: any) => void>()
    }, [])


    /*const addkeydownevent =  (name: string, callBackEvent: (e: any) => void) => {
        if (keyDownEvent.has(name)) {
            keyDownEvent.delete(name);
        }
        keyDownEvent.set(name, callBackEvent);
    }*/

    const addkeydownevent = useCallback((name: string, callBackEvent: (e: any) => void) => {
        if (keyDownEvent.has(name)) {
            keyDownEvent.delete(name);
        }
        keyDownEvent.set(name, callBackEvent);
        // eslint-disable-next-line
    }, []);


    useEffect(() => {
        dispatch(changeStoreLanguage(languageCode))
        // eslint-disable-next-line
    }, [languageCode])

    useEffect(() => {
        if (isDirectionRTL) {
            document.documentElement.classList.add('rtl');
            document.documentElement.setAttribute('data-direction', 'rtl')
        } else {
            document.documentElement.classList.remove('rtl');
            document.documentElement.setAttribute('data-direction', 'ltr')
        }
        if (themeColor) {
            styleSheetLink.href = `/css/${themeColor}.min.css`;
        }
    }, [themeColor, isDirectionRTL]);

    useEffect(() => {
        if (themeType === THEME_TYPE_DARK) {
            document.body.classList.add('dark-theme');
            styleSheetLink.href = "/css/dark_theme.min.css";
        } else if (document.body.classList.contains('dark-theme')) {
            document.body.classList.remove('dark-theme');
            styleSheetLink.href = "";
        }
    }, [themeType]);
    useEffect(() => {
        document.onkeydown = function (e) {
            keyDownEvent.forEach(value => {
                if (value !== undefined) {
                    try {
                        value(e);
                    } catch {

                    }
                }
            })
            if (e.key === "F2" || e.key === "F7" || e.key === "F8" || e.key === "F3") {
                return false;
            }


        }
    }, [keyDownEvent]);
    useEffect(() => {
        setLayoutType(layoutType);
        setNavStyle(navStyle);
    }, [layoutType, navStyle]);

    const login = useMemo(() => {
            return <Login/>
        },
        // eslint-disable-next-line
        [locale]);

    const appInfo: AppInfo = AppInfoGetter() ?? AppInfoGetter.Default;
    const documentTitle = getSubProjectInfo(getSubProjectFromTitle(), ((appInfo?.project?.name ?? "") + " " + (appInfo?.project?.title ?? ""))).subProjectInfo?.title ?? " ";
    if (documentTitle === " ") {
        document.title = "Erp";
    } else {
        document.title = documentTitle;
    }
    return (

        <ConfigProvider locale={antdLocale} direction={isDirectionRTL ? "rtl" : "ltr"}>
            <I18nProvider>
                <EnumProvider>
                    <MinoNotificationProvider>
                        <Suspense fallback={<CircularProgress/>}>
                            <ApiSpinner>
                                <BrowserRouter>
                                    <AppContext.Provider value={{addkeydownevent, appinfo: appInfo}}>
                                        <Routes>
                                            <Route path="/:projectType/login" element={login}/>
                                            <Route path="/login" element={<Navigate replace
                                                                                    to={`/${getSubProject() ?? defaultSubProjectName}/login`}/>}/>
                                            <Route
                                                path="/*"
                                                element={(token === '' || token === undefined || token === null) ?
                                                    <Navigate replace
                                                              to={`/${getSubProject() ?? defaultSubProjectName}/login`}/> :
                                                    <Main/>}
                                            />
                                        </Routes>
                                    </AppContext.Provider>
                                </BrowserRouter>
                            </ApiSpinner>
                        </Suspense>
                    </MinoNotificationProvider>
                </EnumProvider>
            </I18nProvider>

        </ConfigProvider>

    )
};

export default App;